import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface CategoryPillProps {
  name: string;
  slug?: string;
  href?: string;
  variant?: 'default' | 'muted' | 'outline';
  size?: 'sm' | 'md';
  className?: string;
  /** If true, the entire parent card handles navigation - pill is non-interactive */
  cardClickable?: boolean;
}

/**
 * CategoryPill - Standardized category tag component
 * 
 * Uses design tokens for consistent styling across desktop/mobile.
 * Minimum font size enforced for legibility.
 * Truncates with ellipsis on overflow.
 */
export function CategoryPill({
  name,
  slug,
  href,
  variant = 'default',
  size = 'sm',
  className,
  cardClickable = false,
}: CategoryPillProps) {
  const baseStyles = cn(
    // Base styles with token-based sizing
    "inline-flex items-center font-medium uppercase tracking-wide",
    "whitespace-nowrap overflow-hidden text-ellipsis max-w-full",
    "transition-colors rounded-sm",
    // Size variants - minimum font size is --text-sm for legibility
    size === 'sm' && "text-[length:var(--text-sm)] px-2 py-0.5",
    size === 'md' && "text-[length:var(--text-sm)] md:text-[length:var(--text-md)] px-2.5 py-1",
    // Color variants with high contrast
    variant === 'default' && "bg-category text-category-foreground",
    variant === 'muted' && "bg-category-muted text-category-muted-foreground",
    variant === 'outline' && "border border-primary/30 text-primary bg-transparent",
    // Tap target for touch devices
    !cardClickable && "min-h-[28px] md:min-h-[24px]",
    className
  );

  // If card handles clicks, render as non-interactive span
  if (cardClickable) {
    return (
      <span className={baseStyles} title={name}>
        {name}
      </span>
    );
  }

  // Determine the link destination
  const linkHref = href || (slug ? `/news?category=${slug}` : undefined);

  if (linkHref) {
    return (
      <Link 
        to={linkHref}
        className={cn(baseStyles, "hover:opacity-90")}
        title={name}
      >
        {name}
      </Link>
    );
  }

  return (
    <span className={baseStyles} title={name}>
      {name}
    </span>
  );
}
